import axios from "axios";
import * as toastr from "toastr";
import constants from "motw_core/Constants/constants";
import { IDataProvider } from "motw_core/Data/IDataProvider";
import {
    DisplayToastrError,
    HandleError,
} from "source/app/presentation/Shared/HandleError";
import { ShowClientError } from "source/app/presentation/Shared/ErrorBoundary";
import { HandleSystemDateOffset } from "source/app/services/External/ClientDataProvider/Helpers/HandleSystemDateOffset";
import {
    GetClientErrorDetails,
    LogClientDiagnostic,
} from "source/app/services/ClientDiagnostics";
import { IsProduction } from "../Environment/Environment";

const bookOfMindCacheWarningMinimumIntervalMs = 60000;
let lastBookOfMindCacheWarningAt = 0;

class ClientDataProvider implements IDataProvider {
    Login = (
        username: string,
        password: string,
        totpToken: string,
        shouldRememberDevice: boolean,
        // Required because axios and fetch give different fingerprints
        // and we want to use the fingerprint provided by axios
        fingerprint: string | undefined,
        deviceName: string
    ): Promise<Response> =>
        // NOTE: Fetch is used instead of Axios so that the
        // http token will get stored
        fetch(`${this.GetLogicServerBaseURL()}${constants.authLoginRoute}`, {
            method: "POST",
            mode: "cors",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                username,
                password,
                totpToken,
                shouldRememberDevice,
                fingerprint,
                deviceName,
            }),
        });

    LoginAsLocalDevelopmentUser = (username: string): Promise<string> =>
        fetch(`${this.GetLogicServerBaseURL()}auth/dev-login`, {
            method: "POST",
            mode: "cors",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username: username.trim() || undefined }),
        }).then(async (response) => {
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(
                    `Local development login failed: ${response.status} ${errorText}`
                );
            }

            const data = await response.json();
            if (typeof data.username !== "string" || !data.username) {
                throw new Error("Local development login returned no username");
            }

            return data.username;
        });

    GetPasskeyRegistrationOptions = (): Promise<any> =>
        fetch(`${this.GetLogicServerBaseURL()}auth/passkey/register/options`, {
            method: "POST",
            mode: "cors",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
        }).then(this.HandleFetchResponse);

    VerifyPasskeyRegistration = (
        response: any,
        deviceName: string
    ): Promise<any> =>
        fetch(`${this.GetLogicServerBaseURL()}auth/passkey/register/verify`, {
            method: "POST",
            mode: "cors",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ response, deviceName }),
        }).then(this.HandleFetchResponse);

    GetPasskeyLoginOptions = (username: string): Promise<any> =>
        fetch(`${this.GetLogicServerBaseURL()}auth/passkey/login/options`, {
            method: "POST",
            mode: "cors",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username }),
        }).then(this.HandleFetchResponse);

    VerifyPasskeyLogin = (username: string, response: any): Promise<any> =>
        fetch(`${this.GetLogicServerBaseURL()}auth/passkey/login/verify`, {
            method: "POST",
            mode: "cors",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, response }),
        }).then(this.HandleFetchResponse);

    ChangePassword = (
        currentPassword: string,
        newPassword: string
    ): Promise<{ status: string }> =>
        fetch(`${this.GetLogicServerBaseURL()}auth/user/changePassword`, {
            method: "POST",
            mode: "cors",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ currentPassword, newPassword }),
        }).then(this.HandleFetchResponse);

    Logout = (): Promise<Response> =>
        // NOTE: Fetch is used instead of Axios so that the
        // http tokens will get cleared
        fetch(`${this.GetLogicServerBaseURL()}${constants.authLogoutRoute}`, {
            method: "POST",
            mode: "cors",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
        }).then((response) => {
            return response;
        });

    GetRecordsAsync<TEntity>(resource: string): Promise<TEntity[]> {
        return this.GetValueAsync(`sqlServer/${resource}`);
    }

    AddRecordAsync<TReturn>(resource: string, data: object): Promise<TReturn> {
        const logicServerURL = this.GetLogicServerBaseURL();

        if (!logicServerURL) {
            throw new Error("Missing logic server URL!");
        }

        HandleSystemDateOffset(data);
        const url = `${this.GetLogicServerBaseURL()}sqlServer/${resource}`;

        return axios
            .post(url, data, { withCredentials: true })
            .then((response) => response.data)
            .catch(this.HandleAxiosError);
    }

    UpdateRecordAsync<TReturn>(
        resource: string,
        data: object
    ): Promise<TReturn> {
        const logicServerURL = this.GetLogicServerBaseURL();

        if (!logicServerURL) {
            throw new Error("Missing logic server URL!");
        }

        HandleSystemDateOffset(data);
        const url = `${this.GetLogicServerBaseURL()}sqlServer/${resource}`;

        return axios
            .patch(url, data, { withCredentials: true })
            .then((response) => response.data)
            .catch(this.HandleAxiosError);
    }

    DeleteRecordsAsync<TReturn>(resource: string): Promise<TReturn> {
        const logicServerURL = this.GetLogicServerBaseURL();

        if (!logicServerURL) {
            throw new Error("Missing logic server URL!");
        }

        const url = `${this.GetLogicServerBaseURL()}sqlServer/${resource}`;

        return axios
            .delete(url, { withCredentials: true })
            .then((response) => response.data)
            .catch(this.HandleAxiosError);
    }

    GetValueAsync<TReturn>(
        resource: string,
        ParseData?: (data: any) => any,
        CustomErrorHandler?: (error: any) => any
    ): Promise<TReturn> {
        const logicServerURL = this.GetLogicServerBaseURL();

        if (!logicServerURL) {
            throw new Error("Missing logic server URL!");
        }

        const url = `${this.GetLogicServerBaseURL()}${resource}`;

        return axios
            .get(url, { withCredentials: true })
            .then((response) => response.data)
            .then(ParseData)
            .catch(
                CustomErrorHandler ? CustomErrorHandler : this.HandleAxiosError
            );
    }

    PostValueAsync<TReturn>(resource: string, data: object): Promise<TReturn> {
        const logicServerURL = this.GetLogicServerBaseURL();

        if (!logicServerURL) {
            throw new Error("Missing logic server URL!");
        }

        const url = `${this.GetLogicServerBaseURL()}${resource}`;

        return axios
            .post(url, data, { withCredentials: true })
            .then((response) => response.data)
            .catch(this.HandleAxiosError);
    }

    async PostBookOfMindPageSpeechJobAsync<TReturn>(
        resource: string,
        data: object,
        diagnosticDetails: Record<string, unknown>
    ): Promise<TReturn> {
        const logicServerURL = this.GetLogicServerBaseURL();
        if (!logicServerURL) {
            throw new Error("Missing logic server URL!");
        }

        const requestStartedAt = performance.now();
        const url = `${logicServerURL}${resource}`;

        try {
            const response = await axios.post(url, data, {
                withCredentials: true,
            });
            const responseDetails = {
                ...diagnosticDetails,
                status: response.status,
                statusText: response.statusText,
                contentType: response.headers["content-type"],
                contentLength: response.headers["content-length"],
                responseDataType: GetResponseDataType(response.data),
                hasJobId: HasPageSpeechJobId(response.data),
                elapsedMs: Math.round(performance.now() - requestStartedAt),
            };
            LogClientDiagnostic(
                "book-of-mind-speech",
                "page-audio-start-response",
                responseDetails,
                HasPageSpeechJobId(response.data) ? "info" : "error"
            );

            if (!HasPageSpeechJobId(response.data)) {
                throw new Error(
                    "Book of Mind page audio returned an unexpected response."
                );
            }

            return response.data as TReturn;
        } catch (error) {
            LogClientDiagnostic(
                "book-of-mind-speech",
                "page-audio-start-error",
                {
                    ...diagnosticDetails,
                    status: error?.response?.status,
                    contentType: error?.response?.headers?.["content-type"],
                    elapsedMs: Math.round(performance.now() - requestStartedAt),
                    error: GetClientErrorDetails(error),
                },
                "error"
            );

            if (error?.response) {
                this.HandleAxiosError(error);
            }

            throw error;
        }
    }

    async GetBookOfMindPageSpeechJobAsync<TReturn>(
        resource: string,
        diagnosticDetails: Record<string, unknown>,
        shouldLogResponse = true
    ): Promise<TReturn> {
        const logicServerURL = this.GetLogicServerBaseURL();
        if (!logicServerURL) {
            throw new Error("Missing logic server URL!");
        }

        const requestStartedAt = performance.now();
        const url = `${logicServerURL}${resource}`;

        try {
            const response = await axios.get(url, { withCredentials: true });
            if (shouldLogResponse) {
                LogClientDiagnostic(
                    "book-of-mind-speech",
                    "page-audio-job-response",
                    {
                        ...diagnosticDetails,
                        status: response.status,
                        statusText: response.statusText,
                        contentType: response.headers["content-type"],
                        jobId: response.data?.jobId,
                        jobStatus: response.data?.status,
                        elapsedMs: Math.round(
                            performance.now() - requestStartedAt
                        ),
                    }
                );
            }

            if (
                !HasPageSpeechJobId(response.data) ||
                typeof response.data?.status !== "string"
            ) {
                throw new Error(
                    "Book of Mind page audio returned an invalid job response."
                );
            }

            return response.data as TReturn;
        } catch (error) {
            LogClientDiagnostic(
                "book-of-mind-speech",
                "page-audio-job-error",
                {
                    ...diagnosticDetails,
                    status: error?.response?.status,
                    contentType: error?.response?.headers?.["content-type"],
                    elapsedMs: Math.round(performance.now() - requestStartedAt),
                    error: GetClientErrorDetails(error),
                },
                "error"
            );

            if (error?.response) {
                this.HandleAxiosError(error);
            }

            throw error;
        }
    }

    async GetAudioAsync(
        resource: string,
        diagnosticDetails?: Record<string, unknown>
    ): Promise<Blob> {
        const logicServerURL = this.GetLogicServerBaseURL();

        if (!logicServerURL) {
            throw new Error("Missing logic server URL!");
        }

        const requestStartedAt = performance.now();

        try {
            const response = await fetch(`${logicServerURL}${resource}`, {
                method: "GET",
                mode: "cors",
                credentials: "include",
            });
            const responseDetails = {
                ...diagnosticDetails,
                status: response.status,
                statusText: response.statusText,
                contentType: response.headers.get("content-type"),
                contentLength: response.headers.get("content-length"),
                elapsedMs: Math.round(performance.now() - requestStartedAt),
            };

            if (!response.ok) {
                LogClientDiagnostic(
                    "book-of-mind-speech",
                    "page-audio-audio-response",
                    responseDetails,
                    "error"
                );
                const errorText = await response.text();
                throw new Error(
                    `Logic server audio request failed: ${response.status} ${response.statusText} - ${errorText}`
                );
            }

            const audioBlob = await response.blob();
            LogClientDiagnostic(
                "book-of-mind-speech",
                "page-audio-audio-body-read",
                {
                    ...responseDetails,
                    byteLength: audioBlob.size,
                    blobType: audioBlob.type,
                    elapsedMs: Math.round(performance.now() - requestStartedAt),
                }
            );
            return audioBlob;
        } catch (error) {
            LogClientDiagnostic(
                "book-of-mind-speech",
                "page-audio-audio-error",
                {
                    ...diagnosticDetails,
                    elapsedMs: Math.round(performance.now() - requestStartedAt),
                    error: GetClientErrorDetails(error),
                },
                "error"
            );
            throw error;
        }
    }

    async PostAudioAsync(resource: string, data: object): Promise<Blob> {
        const logicServerURL = this.GetLogicServerBaseURL();
        const isBookOfMindSpeech =
            resource === "workflownodes/bookofmind/speech" ||
            resource === "workflownodes/bookofmind/pageSpeech";
        const requestStartedAt = performance.now();
        const speechData = data as {
            pitch?: number;
            speakingRate?: number;
            text?: unknown;
            title?: unknown;
            quotes?: unknown;
            voiceName?: string;
        };
        const pageTextLength = Array.isArray(speechData.quotes)
            ? speechData.quotes.reduce(
                  (total, quote) =>
                      total +
                      (typeof quote === "string" ? quote.length : 0),
                  typeof speechData.title === "string"
                      ? speechData.title.length
                      : 0
              )
            : undefined;
        const requestDetails = {
            resource,
            ...(isBookOfMindSpeech
                ? {
                      voiceName: speechData.voiceName,
                      textLength:
                          typeof speechData.text === "string"
                              ? speechData.text.length
                              : pageTextLength,
                      quoteCount: Array.isArray(speechData.quotes)
                          ? speechData.quotes.length
                          : undefined,
                      speakingRate: speechData.speakingRate,
                      pitch: speechData.pitch,
                  }
                : {}),
        };

        if (isBookOfMindSpeech) {
            LogClientDiagnostic(
                "book-of-mind-speech",
                "fetch-request",
                requestDetails
            );
        }

        if (!logicServerURL) {
            throw new Error("Missing logic server URL!");
        }

        try {
            const response = await fetch(`${logicServerURL}${resource}`, {
                method: "POST",
                mode: "cors",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });

            if (isBookOfMindSpeech) {
                LogClientDiagnostic(
                    "book-of-mind-speech",
                    "fetch-response",
                    {
                        ...requestDetails,
                        status: response.status,
                        statusText: response.statusText,
                        ok: response.ok,
                        contentType: response.headers.get("content-type"),
                        contentLength: response.headers.get("content-length"),
                        elapsedMs: Math.round(
                            performance.now() - requestStartedAt
                        ),
                    }
                );
            }

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(
                    `Logic server audio request failed: ${response.status} ${response.statusText} - ${errorText}`
                );
            }

            if (
                response.headers.get("X-Book-Of-Mind-Cache-Warning") === "true"
            ) {
                ReportBookOfMindCacheWarning(requestDetails);
            }

            const audioBlob = await response.blob();

            if (isBookOfMindSpeech) {
                LogClientDiagnostic(
                    "book-of-mind-speech",
                    "fetch-body-read",
                    {
                        ...requestDetails,
                        byteLength: audioBlob.size,
                        blobType: audioBlob.type,
                        elapsedMs: Math.round(
                            performance.now() - requestStartedAt
                        ),
                    }
                );
            }

            return audioBlob;
        } catch (error) {
            if (isBookOfMindSpeech) {
                LogClientDiagnostic(
                    "book-of-mind-speech",
                    "fetch-error",
                    {
                        ...requestDetails,
                        elapsedMs: Math.round(
                            performance.now() - requestStartedAt
                        ),
                        error: GetClientErrorDetails(error),
                    },
                    "error"
                );
            }

            throw error;
        }
    }

    /**
     * Custom error handling for Axios errors
     */
    private HandleAxiosError(e: any) {
        LogClientDiagnostic(
            "client-network",
            "request-error",
            {
                method: String(e?.config?.method || "GET").toUpperCase(),
                path: GetRequestPathForDiagnostics(e?.config?.url),
                status: e?.response?.status,
                statusText: e?.response?.statusText,
                hasResponse: Boolean(e?.response),
            },
            "error"
        );

        if (!e.response) {
            DisplayToastrError(
                `Unable to connect to logic server at ${this.GetLogicServerBaseURL()}`,
                "Connection error",
                { timeOut: 500 }
            );

            return;
        }

        const isMissingAuthorizationToken =
            e.response.status === 500 &&
            typeof e.response.data === "string" &&
            e.response.data.includes("Missing authorization token cookie");

        if (e.response.status === 401 || isMissingAuthorizationToken) {
            ShowClientError(e);
            return;
        }

        if (e && e.response && e.response.data) {
            HandleError(e.response.data);
            return;
        }

        HandleError(e);
    }

    private async HandleFetchResponse(response: Response) {
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(
                `Server error: ${response.status} ${response.statusText} - ${errorText}`
            );
        }

        return response.json();
    }

    private GetLogicServerBaseURL() {
        if (!IsProduction()) {
            return `${location.origin}/`;
        }

        return process.env.LOGIC_SERVER_URL;
    }
}

function GetRequestPathForDiagnostics(url: unknown) {
    if (typeof url !== "string") {
        return "unknown";
    }

    try {
        return new URL(url, location.origin).pathname;
    } catch (_error) {
        return "unknown";
    }
}

function ReportBookOfMindCacheWarning(details: Record<string, unknown>) {
    LogClientDiagnostic(
        "book-of-mind-speech-cache",
        "cache-warning",
        details,
        "error"
    );

    const now = Date.now();
    if (
        now - lastBookOfMindCacheWarningAt >=
        bookOfMindCacheWarningMinimumIntervalMs
    ) {
        lastBookOfMindCacheWarningAt = now;
        toastr.error(
            "Audio cache unavailable; playback continued using WaveNet.",
            "Book of Mind audio cache"
        );
    }
}

function HasPageSpeechJobId(data: unknown): boolean {
    return (
        typeof data === "object" &&
        data !== null &&
        typeof (data as { jobId?: unknown }).jobId === "string"
    );
}

function GetResponseDataType(data: unknown): string {
    if (data === null) {
        return "null";
    }

    if (Array.isArray(data)) {
        return "array";
    }

    if (typeof Blob !== "undefined" && data instanceof Blob) {
        return "blob";
    }

    return typeof data;
}

export default new ClientDataProvider();
